import { useEffect, type ReactNode } from 'react';

/**
 * Generic, reusable confirmation dialog — the in-app replacement for
 * `window.confirm()`. Controlled (`open` + `onConfirm`/`onCancel`); matches the
 * codebase's hand-rolled overlay convention (fixed inset-0, bg-black/70,
 * backdrop-click + Escape to cancel, role="dialog"). Use for any "are you sure?"
 * flow — bulk delete, discard-changes guards, destructive actions, etc.
 *
 *   <ConfirmDialog
 *     open={confirming}
 *     title="Delete 10 iterations?"
 *     message="This permanently deletes their files and can't be undone."
 *     confirmLabel="Delete"
 *     destructive
 *     busy={deleting}
 *     onConfirm={doDelete}
 *     onCancel={() => setConfirming(false)}
 *   />
 *
 * Pass `children` instead of (or in addition to) `message` for richer content
 * (e.g. a scrollable list of what will be affected).
 */
export function ConfirmDialog({
  open,
  title,
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message?: string;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (red) for delete-type actions. */
  destructive?: boolean;
  /** Disable buttons + show a working label while the action runs. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // Escape cancels (ignored while busy so a mid-flight action isn't abandoned).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={() => {
        if (!busy) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-border bg-bg-secondary p-5 shadow-xl"
      >
        <h3
          id="confirm-dialog-title"
          className="text-sm font-semibold text-text-primary"
        >
          {title}
        </h3>
        {message && (
          <p className="mt-2 text-xs leading-relaxed text-text-secondary">{message}</p>
        )}
        {children && <div className="mt-2">{children}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded border border-border px-4 py-2 text-sm text-text-muted transition-colors hover:text-text-primary disabled:opacity-40"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={
              'rounded px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 ' +
              (destructive ? 'bg-bearish' : 'bg-accent')
            }
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
